import React, { useState, useEffect } from "react";
import {
    Container, Typography, Box, FormControl,
    RadioGroup, FormControlLabel, Radio, Select, MenuItem,
    Table, TableBody, TableCell, TableHead, TableRow,
    Paper, Card, CardContent, Chip, Alert
} from "@mui/material";
import { Line } from "react-chartjs-2";
import TimelineIcon from '@mui/icons-material/Timeline';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BarChartIcon from '@mui/icons-material/BarChart';
import { styled } from '@mui/material/styles';
import { Chart as ChartJS, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Title } from "chart.js";

import api from "../api";
import MemoryMetrics from "./srs/DeckMemoryMetrics";

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Title);

// Styled components for enhanced visual hierarchy
const DashboardContainer = styled(Container)(({ theme }) => ({
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(4),
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
}));

const StatCard = styled(Card)(({ theme }) => ({
    background: 'linear-gradient(145deg, #f0f4f8 0%, #e6eaf0 100%)',
    transition: 'transform 0.3s ease-in-out',
    '&:hover': {
        transform: 'scale(1.03)',
    },
}));

const Dashboard = ({ token }) => {
    const [chartData, setChartData] = useState(null);
    const [totalPending, setTotalPending] = useState(0);
    const [totalUpcoming, setTotalUpcoming] = useState(0);
    const [pendingDecks, setPendingDecks] = useState([]);
    const [aggregator, setAggregator] = useState("user");
    const [selectedDeck, setSelectedDeck] = useState("");
    const [userDecks, setUserDecks] = useState([]);

    useEffect(() => {
        if (!token) return;

        api.get("/decks-for-user")
            .then((res) => {
                setUserDecks(res.data);
            })
            .catch((err) => {
                console.error("Error fetching user decks:", err);
            });

        fetchDashboardData("user", "");
    }, [token]);

    const fetchDashboardData = (agg, deckId) => {
        let url = `/dashboard?aggregator=${agg}`;
        if (agg === "deck" && deckId) {
            url += `&deckId=${deckId}`;
        }

        api.get(url)
            .then((res) => {
                const data = res.data;

                setTotalPending(data.totalPendingReviews);
                setTotalUpcoming(data.totalUpcomingReviews24);

                setPendingDecks(data.pendingByDeck || []);

                const reviewCounts = new Array(24).fill(0);

                const now = new Date();
                data.upcomingReviews.forEach((item) => {
                    const hour = new Date(item.nextReviewTime).getHours();
                    reviewCounts[hour]++;
                });

                setChartData({
                    labels: [...Array(24).keys()].map((h) => `${h}:00`),
                    datasets: [
                        {
                            label: "Upcoming Review Items",
                            data: reviewCounts,
                            fill: false,
                            borderColor: "#1976d2",
                            backgroundColor: "rgba(25, 118, 210, 0.5)",
                            pointHoverBackgroundColor: "#ff9800",
                            tension: 0.2,
                        },
                    ],
                });
            })
            .catch((err) => {
                console.error("Error fetching dashboard data:", err);
            });
    };

    const handleAggregatorChange = (event) => {
        const newAggregator = event.target.value;
        setAggregator(newAggregator);

        if (newAggregator === "user") {
            fetchDashboardData("user", "");
            setSelectedDeck("");
        }
    };

    const handleDeckChange = (event) => {
        const deckId = event.target.value;
        setSelectedDeck(deckId);

        fetchDashboardData("deck", deckId);
    };

    return (
        <DashboardContainer maxWidth="lg">
            <Typography 
                variant="h4" 
                gutterBottom 
                sx={{ 
                    textAlign: 'center', 
                    color: 'primary.main', 
                    fontWeight: 'bold',
                    mb: 4 
                }}
            >
                <TimelineIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
                Spaced Repetition Dashboard
            </Typography>

            <MemoryMetrics />

            <Box sx={{ display: 'flex', gap: 3, mt: 4 }}>
                <Box sx={{ flex: '0 0 250px' }}>
                    <Typography 
                        variant="h6" 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            mb: 2 
                        }}
                    >
                        <AssessmentIcon sx={{ mr: 1 }} /> 
                        Quick Stats
                    </Typography>

                    <StatCard elevation={3}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle1">Pending Reviews</Typography>
                                <Chip 
                                    label={totalPending} 
                                    color="warning" 
                                    variant="outlined" 
                                />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle1">Upcoming (24h)</Typography>
                                <Chip 
                                    label={totalUpcoming} 
                                    color="info" 
                                    variant="outlined" 
                                />
                            </Box>
                        </CardContent>
                    </StatCard>
                </Box>

                <Box sx={{ flex: 1 }}>
                    <Typography 
                        variant="h6" 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            mb: 2 
                        }}
                    >
                        <BarChartIcon sx={{ mr: 1 }} /> 
                        Pending Reviews by Deck
                    </Typography>

                    <Paper elevation={2} sx={{ maxHeight: 300, overflow: 'auto' }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.light', color: 'white' }}>
                                        Deck
                                    </TableCell>
                                    <TableCell 
                                        sx={{ 
                                            fontWeight: 'bold', 
                                            bgcolor: 'primary.light', 
                                            color: 'white', 
                                            textAlign: 'right' 
                                        }}
                                    >
                                        Pending
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pendingDecks.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={2}>
                                            <Alert severity="info">No pending reviews at the moment!</Alert>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    pendingDecks.map((deck, index) => (
                                        <TableRow 
                                            key={deck.deckId} 
                                            hover
                                            sx={{ 
                                                '&:nth-of-type(even)': { 
                                                    backgroundColor: 'action.hover' 
                                                } 
                                            }}
                                        >
                                            <TableCell>
                                                <Typography 
                                                    component="a" 
                                                    href={`/review/${deck.deckId}`}
                                                    sx={{ 
                                                        color: 'primary.main', 
                                                        textDecoration: 'none',
                                                        '&:hover': { 
                                                            textDecoration: 'underline' 
                                                        }
                                                    }}
                                                >
                                                    {deck.title || "Untitled Deck"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Chip 
                                                    label={deck.count} 
                                                    size="small" 
                                                    color="secondary" 
                                                    variant="outlined" 
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </Paper>
                </Box>
            </Box>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <FormControl>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography>Chart View:</Typography>
                        <RadioGroup 
                            row 
                            value={aggregator} 
                            onChange={handleAggregatorChange}
                        >
                            <FormControlLabel 
                                value="user" 
                                control={<Radio color="primary" />} 
                                label="User Level" 
                            />
                            <FormControlLabel 
                                value="deck" 
                                control={<Radio color="primary" />} 
                                label="Deck Level" 
                            />
                        </RadioGroup>
                        {aggregator === "deck" && (
                            <Select
                                value={selectedDeck}
                                onChange={handleDeckChange}
                                displayEmpty
                                variant="outlined"
                                sx={{ minWidth: 200 }}
                            >
                                <MenuItem value="">
                                    <em>Select Deck</em>
                                </MenuItem>
                                {userDecks.map((deck) => (
                                    <MenuItem key={deck._id} value={deck._id}>
                                        {deck.title}
                                    </MenuItem>
                                ))}
                            </Select>
                        )}
                    </Box>
                </FormControl>
            </Box>

            <Box sx={{ mt: 4 }}>
                <Typography 
                    variant="h6" 
                    gutterBottom 
                    sx={{ textAlign: 'center' }}
                >
                    Upcoming Reviews Distribution (Next 24 Hours)
                </Typography>

                {chartData ? (
                    <Paper 
                        elevation={3} 
                        sx={{ 
                            p: 3, 
                            borderRadius: 2, 
                            height: 400, 
                            backgroundColor: 'background.paper' 
                        }}
                    >
                        <Line
                            data={chartData}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    tooltip: {
                                        callbacks: {
                                            label: (context) => `${context.raw} review items`,
                                        },
                                    },
                                },
                                scales: {
                                    y: {
                                        beginAtZero: true,
                                        precision: 0,
                                    },
                                },
                            }}
                        />
                    </Paper>
                ) : (
                    <Alert severity="info">Loading review data...</Alert>
                )}
            </Box>
        </DashboardContainer>
    );
};

export default Dashboard;